import React, { createContext, useState, useEffect, useMemo, useContext } from 'react';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { lightTheme } from '../theme';
import { useAppSettings } from './AppSettingsContext';

type ThemeMode = 'light' | 'dark';

interface ThemeContextType {
  mode: ThemeMode;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useThemeMode = () => {
  const context = useContext(ThemeContext);
  if (!context) throw new Error('useThemeMode must be used within ThemeContextProvider');
  return context;
};

export const ThemeContextProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { settings } = useAppSettings();

  const [mode, setMode] = useState<ThemeMode>('light');

  useEffect(() => {
    const storedMode = localStorage.getItem('themeMode') as ThemeMode;
    if (storedMode) setMode(storedMode);
  }, []);

  const toggleTheme = () => {
    const newMode: ThemeMode = mode === 'light' ? 'dark' : 'light';
    setMode(newMode);
    localStorage.setItem('themeMode', newMode);
  };

  const theme = useMemo(() => {
    if (!settings?.theme) return lightTheme;

    return createTheme({
      palette: {
        mode: settings.theme.themeType,
        ...settings.theme.palette,
      },
    });
  }, [settings?.theme]);

  return (
    <ThemeContext.Provider value={{ mode, toggleTheme }}>
      <ThemeProvider theme={theme}>{children}</ThemeProvider>
    </ThemeContext.Provider>
  );
};
